import { defineStore } from 'pinia';
import { api } from '../api/client';

export type KycStatus = 'NONE' | 'PENDING' | 'APPROVED';

export interface AuthUser {
  id: string;
  email: string;
  username: string | null;
  role: 'USER' | 'ADMIN' | 'KYC_ADMIN';
  emailVerified: boolean;
  banned: boolean;
  firstName: string | null;
  lastName: string | null;
  country: string | null;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  phone: string | null;
  birthDate: string | null;
  kycStatus: KycStatus;
  kycLastChangedAt: string | null;
  walletAvailableCents: number;
  walletHeldCents: number;
  createdAt: string;
}

interface State {
  user: AuthUser | null;
  loading: boolean;
  initialized: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
    loading: false,
    initialized: false,
  }),
  getters: {
    isAuthenticated: (s) => s.user !== null,
    isAdmin: (s) => s.user?.role === 'ADMIN',
    /** May open /admin at all: ADMIN, or KYC_ADMIN (KYC queue + read-only user detail only). */
    isStaff: (s) => s.user?.role === 'ADMIN' || s.user?.role === 'KYC_ADMIN',
  },
  actions: {
    async fetchUser() {
      this.loading = true;
      try {
        const { data } = await api.get<AuthUser>('/users/me');
        this.user = data;
      } catch {
        this.user = null;
      } finally {
        this.loading = false;
        this.initialized = true;
      }
    },
    async login(
      email: string,
      password: string,
      altcha?: string,
    ): Promise<{ twoFactorRequired: boolean; method?: 'TOTP' | 'EMAIL'; challengeToken?: string }> {
      const { data } = await api.post<{
        twoFactorRequired?: boolean;
        method?: 'TOTP' | 'EMAIL';
        challengeToken?: string;
      }>('/auth/login', {
        email,
        password,
        altcha: altcha ?? (import.meta.env.DEV ? 'dev-bypass' : ''),
      });
      // 2FA enabled: no session cookie was set — caller must collect the code
      // and call verifyTwoFactor(). Don't fetchUser() yet.
      if (data?.twoFactorRequired) {
        return { twoFactorRequired: true, method: data.method, challengeToken: data.challengeToken };
      }
      await this.fetchUser();
      return { twoFactorRequired: false };
    },
    async verifyTwoFactor(challengeToken: string, code: string) {
      await api.post('/auth/2fa/verify', { challengeToken, code });
      await this.fetchUser();
    },
    async register(email: string, password: string, username?: string, altcha?: string) {
      await api.post('/auth/register', {
        email,
        password,
        username,
        altcha: altcha ?? (import.meta.env.DEV ? 'dev-bypass' : ''),
      });
      await this.fetchUser();
    },
    async logout() {
      try {
        await api.post('/auth/logout');
      } catch {
        // ignore
      }
      this.user = null;
    },
  },
});
